import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CreditCard,
  Calendar,
  AlertTriangle,
  Download,
  RefreshCw,
  XCircle,
  RotateCcw,
  ExternalLink,
  Clock,
  Ban,
  Loader2,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface StripeInvoice {
  id: string;
  number: string | null;
  amountDue: number;
  amountPaid: number;
  currency: string;
  status: "draft" | "open" | "paid" | "uncollectible" | "void";
  created: number;
  hostedInvoiceUrl: string | null;
  invoicePdf: string | null;
}

export interface StripeFailedPayment {
  id: string;
  amount: number;
  currency: string;
  created: number;
  failureMessage: string | null;
  failureCode: string | null;
  invoiceId: string | null;
}

export interface StripePaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

export interface StripeUpcomingInvoice {
  amountDue: number;
  currency: string;
  periodEnd: number;
  lines: { description: string; amount: number }[];
}

export interface StripeSubscriptionDetails {
  id: string;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: number;
  currentPeriodStart: number;
  planName: string | null;
  planAmount: number | null;
  planInterval: string | null;
}

export interface StripeSectionProps {
  companyId: string;
  stripeCustomerId: string | null;
  isSuperAdmin: boolean;
  canAct: boolean;
  invoices: StripeInvoice[];
  failedPayments: StripeFailedPayment[];
  paymentMethods: StripePaymentMethod[];
  upcomingInvoice: StripeUpcomingInvoice | null;
  subscription: StripeSubscriptionDetails | null;
  isLoading: boolean;
  onRetryInvoice: (invoiceId: string) => void;
  onCancelSubscription: (reason: string) => void;
  onRefundInvoice: (invoiceId: string, amount: number, reason: string) => void;
  isRetrying?: boolean;
  isCancelling?: boolean;
  isRefunding?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount: number, currency = "eur"): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

function formatDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const INVOICE_STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  paid: { label: "Payée", variant: "default" },
  open: { label: "En attente", variant: "outline" },
  draft: { label: "Brouillon", variant: "secondary" },
  uncollectible: { label: "Irrécouvrable", variant: "destructive" },
  void: { label: "Annulée", variant: "secondary" },
};

const SUB_STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  active: { label: "Actif", variant: "default" },
  past_due: { label: "Retard", variant: "destructive" },
  canceled: { label: "Annulé", variant: "destructive" },
  incomplete: { label: "Incomplet", variant: "outline" },
  incomplete_expired: { label: "Expiré", variant: "destructive" },
  trialing: { label: "Essai", variant: "outline" },
  unpaid: { label: "Impayé", variant: "destructive" },
  paused: { label: "Pausé", variant: "secondary" },
};

// ─── Component ───────────────────────────────────────────────────────────────

export function StripeSection({
  companyId,
  stripeCustomerId,
  isSuperAdmin,
  canAct,
  invoices,
  failedPayments,
  paymentMethods,
  upcomingInvoice,
  subscription,
  isLoading,
  onRetryInvoice,
  onCancelSubscription,
  onRefundInvoice,
  isRetrying = false,
  isCancelling = false,
  isRefunding = false,
}: StripeSectionProps) {
  const [refundDialog, setRefundDialog] = useState<{
    invoiceId: string;
    maxAmount: number;
  } | null>(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [cancelDialog, setCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  if (!stripeCustomerId) {
    return (
      <Card className="border-muted bg-muted/30">
        <CardContent className="py-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <CreditCard className="h-8 w-8 text-muted-foreground/50" />
            <div>
              <div className="text-sm font-medium text-muted-foreground">
                Pas de compte Stripe
              </div>
              <div className="text-xs text-muted-foreground/70 mt-1">
                Cette entreprise n'a pas de stripeCustomerId. Les données Stripe
                seront disponibles après la première souscription.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Chargement des données Stripe…
      </div>
    );
  }

  const defaultPm =
    paymentMethods.find((pm) => pm.isDefault) ?? paymentMethods[0] ?? null;
  const subStatus = subscription
    ? SUB_STATUS_CONFIG[subscription.status] ?? SUB_STATUS_CONFIG.active
    : null;

  return (
    <div className="space-y-6">
      {/* ─── Top cards row ─── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Payment method */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Moyen de paiement
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            {defaultPm ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Carte</span>
                  <span className="font-mono font-medium uppercase">
                    {defaultPm.brand} •••• {defaultPm.last4}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Expire</span>
                  <span>
                    {String(defaultPm.expMonth).padStart(2, "0")}/
                    {defaultPm.expYear}
                  </span>
                </div>
                {defaultPm.isDefault && (
                  <Badge variant="outline" className="text-xs mt-1">
                    Par défaut
                  </Badge>
                )}
              </>
            ) : (
              <div className="text-muted-foreground text-center py-2">
                Aucune carte enregistrée
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming invoice / next renewal */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Prochain renouvellement
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            {upcomingInvoice ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">
                    {formatDate(upcomingInvoice.periodEnd)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Montant</span>
                  <span className="font-medium">
                    {formatCurrency(
                      upcomingInvoice.amountDue,
                      upcomingInvoice.currency
                    )}
                  </span>
                </div>
                {upcomingInvoice.lines.length > 0 && (
                  <div className="text-xs text-muted-foreground pt-1 border-t border-border">
                    {upcomingInvoice.lines.map((l, i) => (
                      <div key={i}>{l.description}</div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted-foreground text-center py-2">
                Aucun renouvellement prévu
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription status */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Abonnement Stripe
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            {subscription ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Statut</span>
                  <Badge variant={subStatus?.variant ?? "outline"}>
                    {subStatus?.label ?? subscription.status}
                  </Badge>
                </div>
                {subscription.planName && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Plan</span>
                    <span className="font-medium">{subscription.planName}</span>
                  </div>
                )}
                {subscription.planAmount != null && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Montant</span>
                    <span>
                      {formatCurrency(subscription.planAmount)}/
                      {subscription.planInterval === "year" ? "an" : "mois"}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Fin période</span>
                  <span>{formatDate(subscription.currentPeriodEnd)}</span>
                </div>
                {subscription.cancelAtPeriodEnd && (
                  <div className="flex items-center gap-2 text-destructive text-xs mt-1">
                    <Ban className="h-3.5 w-3.5" />
                    Annulation prévue en fin de période
                  </div>
                )}
                {isSuperAdmin &&
                  canAct &&
                  !subscription.cancelAtPeriodEnd &&
                  subscription.status === "active" && (
                    <div className="pt-2 border-t border-border">
                      <Button
                        size="sm"
                        variant="destructive"
                        className="w-full"
                        onClick={() => setCancelDialog(true)}
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1" />
                        Annuler l'abonnement
                      </Button>
                    </div>
                  )}
              </>
            ) : (
              <div className="text-muted-foreground text-center py-2">
                Aucun abonnement actif
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Failed payments alert ─── */}
      {failedPayments.length > 0 && (
        <Card className="border-destructive/30 bg-destructive/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Tentatives échouées ({failedPayments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Montant</TableHead>
                  <TableHead className="text-xs">Raison</TableHead>
                  {isSuperAdmin && canAct && (
                    <TableHead className="text-xs w-[100px]">Action</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {failedPayments.map((fp) => (
                  <TableRow key={fp.id}>
                    <TableCell className="text-xs">
                      {formatDate(fp.created)}
                    </TableCell>
                    <TableCell className="text-xs font-medium text-destructive">
                      {formatCurrency(fp.amount, fp.currency)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                      {fp.failureMessage ?? fp.failureCode ?? "Inconnue"}
                    </TableCell>
                    {isSuperAdmin && canAct && (
                      <TableCell>
                        {fp.invoiceId && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            disabled={isRetrying}
                            onClick={() => onRetryInvoice(fp.invoiceId!)}
                          >
                            <RefreshCw className="h-3 w-3 mr-1" />
                            Relancer
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ─── Invoices table ─── */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Dernières factures
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {invoices.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              Aucune facture
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">N°</TableHead>
                  <TableHead className="text-xs">Date</TableHead>
                  <TableHead className="text-xs">Montant</TableHead>
                  <TableHead className="text-xs">Statut</TableHead>
                  <TableHead className="text-xs w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.slice(0, 10).map((inv) => {
                  const statusCfg =
                    INVOICE_STATUS_CONFIG[inv.status] ??
                    INVOICE_STATUS_CONFIG.open;
                  return (
                    <TableRow key={inv.id}>
                      <TableCell className="text-xs font-mono">
                        {inv.number ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatDate(inv.created)}
                      </TableCell>
                      <TableCell className="text-xs font-medium">
                        {formatCurrency(inv.amountDue, inv.currency)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusCfg.variant} className="text-xs">
                          {statusCfg.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {inv.invoicePdf && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              asChild
                            >
                              <a
                                href={inv.invoicePdf}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Télécharger PDF"
                              >
                                <Download className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                          )}
                          {inv.hostedInvoiceUrl && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 w-7 p-0"
                              asChild
                            >
                              <a
                                href={inv.hostedInvoiceUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                title="Voir sur Stripe"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </a>
                            </Button>
                          )}
                          {isSuperAdmin &&
                            canAct &&
                            inv.status === "paid" &&
                            inv.amountPaid > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-destructive hover:text-destructive"
                                onClick={() =>
                                  setRefundDialog({
                                    invoiceId: inv.id,
                                    maxAmount: inv.amountPaid,
                                  })
                                }
                              >
                                <RotateCcw className="h-3 w-3 mr-1" />
                                Rembourser
                              </Button>
                            )}
                          {isSuperAdmin &&
                            canAct &&
                            (inv.status === "open" ||
                              inv.status === "uncollectible") && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 text-xs"
                                disabled={isRetrying}
                                onClick={() => onRetryInvoice(inv.id)}
                              >
                                <RefreshCw className="h-3 w-3 mr-1" />
                                Relancer
                              </Button>
                            )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* ─── Stripe dashboard link ─── */}
      <div className="flex justify-end">
        <Button variant="outline" size="sm" asChild>
          <a
            href={`https://dashboard.stripe.com/customers/${stripeCustomerId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Voir sur Stripe Dashboard
          </a>
        </Button>
      </div>

      {/* ─── Cancel subscription dialog ─── */}
      <Dialog
        open={cancelDialog}
        onOpenChange={(open) => {
          if (!open) setCancelDialog(false);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <XCircle className="h-5 w-5" />
              Annuler l'abonnement
            </DialogTitle>
            <DialogDescription>
              L'abonnement sera annulé en fin de période de facturation. Le
              client gardera l'accès jusqu'à cette date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Motif{" "}
                <span className="text-muted-foreground font-normal">
                  (obligatoire, min. 20 caractères)
                </span>
              </label>
              <Textarea
                placeholder="Ex : Client a demandé l'annulation par email du 15/04…"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
              />
              {cancelReason.length > 0 &&
                cancelReason.trim().length < 20 && (
                  <p className="text-xs text-destructive">
                    {20 - cancelReason.trim().length} caractères restants
                  </p>
                )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelDialog(false)}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={
                cancelReason.trim().length < 20 || isCancelling
              }
              onClick={() => {
                onCancelSubscription(cancelReason.trim());
                setCancelDialog(false);
                setCancelReason("");
              }}
            >
              {isCancelling
                ? "Annulation…"
                : "Confirmer l'annulation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Refund dialog ─── */}
      <Dialog
        open={!!refundDialog}
        onOpenChange={(open) => {
          if (!open) setRefundDialog(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Remboursement
            </DialogTitle>
            <DialogDescription>
              Le remboursement sera appliqué via Stripe. Max :{" "}
              {refundDialog
                ? formatCurrency(refundDialog.maxAmount)
                : "—"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Type</label>
              <Select
                value={
                  refundAmount === "" ||
                  (refundDialog &&
                    parseInt(refundAmount) === refundDialog.maxAmount)
                    ? "full"
                    : "partial"
                }
                onValueChange={(v) => {
                  if (v === "full" && refundDialog) {
                    setRefundAmount(String(refundDialog.maxAmount));
                  } else {
                    setRefundAmount("");
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">
                    Remboursement total
                  </SelectItem>
                  <SelectItem value="partial">
                    Remboursement partiel
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {refundDialog &&
              parseInt(refundAmount) !== refundDialog.maxAmount && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Montant (centimes)
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max={refundDialog.maxAmount}
                    value={refundAmount}
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder={`Max ${refundDialog.maxAmount}`}
                  />
                </div>
              )}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Motif{" "}
                <span className="text-muted-foreground font-normal">
                  (obligatoire, min. 20 caractères)
                </span>
              </label>
              <Textarea
                placeholder="Ex : Erreur de facturation signalée par le client…"
                value={refundReason}
                onChange={(e) => setRefundReason(e.target.value)}
                rows={3}
              />
              {refundReason.length > 0 &&
                refundReason.trim().length < 20 && (
                  <p className="text-xs text-destructive">
                    {20 - refundReason.trim().length} caractères restants
                  </p>
                )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRefundDialog(null)}
            >
              Annuler
            </Button>
            <Button
              disabled={
                refundReason.trim().length < 20 ||
                !refundAmount ||
                parseInt(refundAmount) <= 0 ||
                isRefunding
              }
              onClick={() => {
                if (!refundDialog) return;
                onRefundInvoice(
                  refundDialog.invoiceId,
                  parseInt(refundAmount),
                  refundReason.trim()
                );
                setRefundDialog(null);
                setRefundAmount("");
                setRefundReason("");
              }}
            >
              {isRefunding ? "Remboursement…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
